'use client';

import { sendGTMEvent } from '@next/third-parties/google';
import * as TabsPrimitive from '@radix-ui/react-tabs';
import { usePathname } from 'next/navigation';
import * as React from 'react';

import { cn } from '@natu/utils/cn';
import { PredefinedGAEvents } from '@natu/utils/events';
import { getEventPayload, type EventProps } from '@natu/utils/getEventPayload';
import { usePageContext } from '@natu/utils/PageContextProvider';

const Tabs = TabsPrimitive.Root;

const TabsList = React.forwardRef<
  React.ElementRef<typeof TabsPrimitive.List>,
  React.ComponentPropsWithoutRef<typeof TabsPrimitive.List>
>(({ className, ...props }, ref) => (
  <TabsPrimitive.List
    ref={ref}
    className={cn(
      'text-muted-foreground inline-flex max-w-full overflow-auto rounded-md p-1',
      className,
    )}
    {...props}
  />
));
TabsList.displayName = TabsPrimitive.List.displayName;

interface TabsTriggerProps extends React.ComponentPropsWithoutRef<typeof TabsPrimitive.Trigger> {
  event?: PredefinedGAEvents;
  eventProps?: EventProps;
}

const TabsTrigger = React.forwardRef<
  React.ElementRef<typeof TabsPrimitive.Trigger>,
  TabsTriggerProps
>(({ className, event, eventProps, ...props }, ref) => {
  const pathname = usePathname();
  const { isCampaign } = usePageContext() || {};
  const eventPayload = event ? getEventPayload({ pathname, event, eventProps, isCampaign }) : {};

  return (
    <TabsPrimitive.Trigger
      ref={ref}
      className={cn(
        'ring-offset-background focus-visible:ring-ring data-[state=active]:bg-background data-[state=active]:text-primary-600 data-[state=active]:bg-primary-100 data-[state=active]:border-b-primary-600 inline-flex items-center justify-center whitespace-nowrap border-b border-b-gray-200 p-3 font-semibold text-gray-500 transition-all focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-offset-2 disabled:pointer-events-none disabled:opacity-50 data-[state=active]:border-b-2',
        className,
      )}
      {...(event && {
        onClick: () => sendGTMEvent({ event, ...eventPayload }),
      })}
      {...props}
    />
  );
});
TabsTrigger.displayName = TabsPrimitive.Trigger.displayName;

const TabsContent = React.forwardRef<
  React.ElementRef<typeof TabsPrimitive.Content>,
  React.ComponentPropsWithoutRef<typeof TabsPrimitive.Content>
>(({ className, ...props }, ref) => (
  <TabsPrimitive.Content
    ref={ref}
    className={cn(
      'ring-offset-background focus-visible:ring-ring mt-2 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-offset-2',
      className,
    )}
    {...props}
  />
));
TabsContent.displayName = TabsPrimitive.Content.displayName;

export { Tabs, TabsList, TabsTrigger, TabsContent };
